import React, { CSSProperties, useRef, useState } from 'react';
import { Reveal } from '@progress/kendo-react-animation';
import { Card as KendoCard, CardHeader, CardTitle, CardBody, CardActions } from '@progress/kendo-react-layout';
import cn from 'clsx';
import CardCollapseButton from '@components-clean/core/card/card-actions/card-collapse-button';
import { CardProps } from '@components-clean/core/card/card.interface';

const DEFAULT_CARD_HEADER_HEIGHT = 41;

const Card = ({
    ref,
    title,
    children,
    bodyClassName = 'card-body',
    bodyStyles,
    headerActions,
    removeHeaderActionStyle,
    headerInfo,
    'data-testid': testDataId,
    padding,
    headerPadding,
    footerActions,
    collapsible,
    collapsed: collapsedProp,
    onCollapsedChange,
    defaultCollapsed,
    ...rest
}: CardProps) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);

    const calcBodyHeight = (): string => {
        const headerHeight = `${headerRef.current?.clientHeight ?? DEFAULT_CARD_HEADER_HEIGHT}px`;
        const footerHeight = `${footerRef.current?.clientHeight ?? 0}px`;

        return `calc(100% - ${headerHeight} - ${footerHeight})`;
    };

    const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false);
    // Use passed in collapsed state if provided. Otherwise, track it internally
    const resolvedCollapsed = collapsedProp ?? collapsed;

    const collapseButton =
        collapsible === true ? (
            <CardCollapseButton
                data-testid={`${testDataId}-card-collapse-button}`}
                collapsed={resolvedCollapsed}
                setCollapsed={(c) => {
                    setCollapsed(c);
                    onCollapsedChange?.(c);
                }}
            />
        ) : null;

    // `Reveal` has the `k-animation-container` class, which has `z-index 10000000 !important` as a style.
    // This causes a whole host of issues when applied to every card in the app.
    // So only apply it to cards that actually specify collapsible functionality.
    const BodyWrapper = collapsible === true ? Reveal : React.Fragment;

    return (
        <KendoCard ref={ref} data-testid={testDataId as string} {...rest}>
            {(title || headerActions) && (
                <div ref={headerRef}>
                    <CardHeader
                        className={cn('card-header k-d-flex k-align-content-center', {
                            'padding-sm': headerPadding === 'small',
                        })}
                    >
                        {title && <CardTitle style={{ flexWrap: 'nowrap' }}>{title}</CardTitle>}
                        <div className="k-ml-auto k-d-flex k-align-items-center">
                            <div className="k-d-flex">{headerInfo}</div>
                            {removeHeaderActionStyle ? (
                                <div>
                                    {headerActions}
                                    {collapseButton}
                                </div>
                            ) : (
                                <div className="k-d-flex k-align-items-baseline k-h-4.5">
                                    {headerActions}
                                    {collapseButton}
                                </div>
                            )}
                        </div>
                    </CardHeader>
                </div>
            )}
            <BodyWrapper>
                {!resolvedCollapsed && (
                    <CardBody
                        className={cn(bodyClassName as string, { '!k-p-0': padding === 'none' })}
                        style={{
                            height: calcBodyHeight(),
                            ...(bodyStyles as CSSProperties),
                        }}
                    >
                        {children}
                    </CardBody>
                )}
            </BodyWrapper>
            {footerActions && (
                <CardActions layout="end" className={cn({ 'padding-sm': headerPadding === 'small' })}>
                    <div ref={footerRef} style={{ float: 'right' }}>
                        {footerActions}
                    </div>
                </CardActions>
            )}
        </KendoCard>
    );
};

export default Card;
